import React, { useEffect } from 'react';
import { useAlert } from '../contexts/AlertContext';

const AlertModal: React.FC = () => {
  const { isOpen, alertState, closeAlert } = useAlert();

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        closeAlert(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, closeAlert]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen || !alertState) return null;

  const getIconColor = () => {
    switch (alertState.variant) {
      case 'success': return 'text-emerald-400';
      case 'warning': return 'text-amber-400';
      case 'error':
      case 'danger': return 'text-rose-400';
      default: return 'text-blue-400';
    }
  };

  const getIcon = () => {
    switch (alertState.variant) {
      case 'success':
        return (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        );
      case 'warning':
        return (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        );
      case 'error':
      case 'danger':
        return (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      default:
        return (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
    }
  };

  const getButtonClass = (variant?: string) => {
    switch (variant) {
      case 'primary':
        return 'btn-primary px-4 py-2 rounded-lg text-sm font-semibold';
      case 'danger':
        return 'px-4 py-2 rounded-lg text-sm font-semibold bg-rose-500/90 hover:bg-rose-500 text-white transition-all hover:shadow-lg hover:shadow-rose-500/25';
      case 'secondary':
      default:
        return 'btn-secondary px-4 py-2 rounded-lg text-sm font-semibold';
    }
  };

  const handleButtonClick = (index: number) => {
    if (alertState.type === 'confirm') {
      // For confirm dialogs: first button (Cancel) = false, second button (Confirm) = true
      closeAlert(index === 1);
    } else {
      // For alert dialogs
      const button = alertState.buttons[index];
      if (button.onClick) {
        button.onClick();
      }
      closeAlert();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm animate-fade-in"
        onClick={() => closeAlert(false)}
      />

      {/* Modal */}
      <div className="relative z-10 w-full max-w-md mx-4 animate-slide-up">
        <div className="card-dark rounded-2xl p-6 shadow-2xl border border-zinc-700/50">
          {/* Header */}
          <div className="flex items-start gap-4">
            <div className={`flex-shrink-0 p-2 rounded-full bg-zinc-800 ${getIconColor()}`}>
              {getIcon()}
            </div>
            <div className="flex-1 min-w-0">
              {alertState.title && (
                <h3 className="text-lg font-semibold text-zinc-100 mb-1">
                  {alertState.title}
                </h3>
              )}
              <p className="text-zinc-300 text-sm leading-relaxed whitespace-pre-wrap">
                {alertState.message}
              </p>
            </div>
          </div>

          {/* Buttons */}
          <div className="flex justify-end gap-3 mt-6">
            {alertState.buttons.map((button, index) => (
              <button
                key={index}
                onClick={() => handleButtonClick(index)}
                className={getButtonClass(button.variant)}
                autoFocus={index === alertState.buttons.length - 1}
              >
                {button.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AlertModal;
